/**
 * Class Status Dropdown
 * Dropdown for managing class status transitions
 *
 * State Transition Rules:
 * - draft → scheduled (Publish)
 * - scheduled → draft (Unpublish) - only if no provisioned labs
 * - scheduled → active (automatic or manual)
 * - active → ended (End Class)
 * - ended: terminal state (no transitions)
 */

import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ChevronDown, Play, Pause, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import type { Class, ClassStatus } from '@cloak-next/shared';

interface ClassStatusDropdownProps {
  classData: Class;
  onStatusChange: (newStatus: ClassStatus) => Promise<void>;
  disabled?: boolean;
}

// Status configuration
const STATUS_CONFIG: Record<ClassStatus, {
  label: string;
  variant: 'default' | 'secondary' | 'outline' | 'destructive';
  icon: React.ComponentType<{ className?: string }>;
}> = {
  draft: {
    label: 'Draft',
    variant: 'secondary',
    icon: Clock,
  },
  scheduled: {
    label: 'Scheduled',
    variant: 'default',
    icon: Play,
  },
  active: {
    label: 'Active',
    variant: 'default',
    icon: Play,
  },
  ended: {
    label: 'Ended',
    variant: 'outline',
    icon: CheckCircle,
  },
};

export function ClassStatusDropdown({
  classData,
  onStatusChange,
  disabled = false,
}: ClassStatusDropdownProps) {
  const [isChanging, setIsChanging] = useState(false);

  const currentStatus = classData.status;
  const config = STATUS_CONFIG[currentStatus];
  const StatusIcon = config.icon;

  // Determine available transitions
  const canPublish = currentStatus === 'draft';
  const canUnpublish = currentStatus === 'scheduled' && !classData.labsProvisioned;
  const canEndClass = currentStatus === 'active';
  const isTerminal = currentStatus === 'ended';

  const handleStatusChange = async (newStatus: ClassStatus) => {
    setIsChanging(true);
    try {
      await onStatusChange(newStatus);
    } finally {
      setIsChanging(false);
    }
  };

  // If terminal state, just show badge without dropdown
  if (isTerminal) {
    return (
      <Badge variant={config.variant} className="cursor-default">
        <StatusIcon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled || isChanging}
          className="gap-1"
        >
          <StatusIcon className="h-4 w-4" />
          <span className="font-medium">{config.label}</span>
          <ChevronDown className="h-4 w-4 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>Change Status</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Draft → Scheduled (Publish) */}
        {canPublish && (
          <DropdownMenuItem
            onClick={() => handleStatusChange('scheduled')}
            disabled={isChanging}
            className="cursor-pointer"
          >
            <Play className="h-4 w-4 mr-2 text-green-600" />
            <div>
              <div className="font-medium">Publish Class</div>
              <div className="text-xs text-muted-foreground">
                Enable student enrollment
              </div>
            </div>
          </DropdownMenuItem>
        )}

        {/* Scheduled → Draft (Unpublish) */}
        {currentStatus === 'scheduled' && (
          <DropdownMenuItem
            onClick={() => canUnpublish && handleStatusChange('draft')}
            disabled={!canUnpublish || isChanging}
            className={canUnpublish ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}
          >
            <Pause className="h-4 w-4 mr-2 text-yellow-600" />
            <div>
              <div className="font-medium">Unpublish</div>
              <div className="text-xs text-muted-foreground">
                {canUnpublish
                  ? 'Return to draft mode'
                  : 'Cannot unpublish: labs are provisioned'}
              </div>
            </div>
          </DropdownMenuItem>
        )}

        {/* Active → Ended (End Class) */}
        {canEndClass && (
          <DropdownMenuItem
            onClick={() => handleStatusChange('ended')}
            disabled={isChanging}
            className="cursor-pointer"
          >
            <CheckCircle className="h-4 w-4 mr-2 text-blue-600" />
            <div>
              <div className="font-medium">End Class</div>
              <div className="text-xs text-muted-foreground">
                Mark class as complete
              </div>
            </div>
          </DropdownMenuItem>
        )}

        {/* Info for non-actionable states */}
        {currentStatus === 'scheduled' && classData.labsProvisioned && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5 text-xs text-muted-foreground flex items-start gap-2">
              <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
              <span>
                Class will become active when start time is reached or when students begin accessing labs.
              </span>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
